import { useEffect, useRef, useState } from 'react'
import { ChevronDown, Pin, PinOff, Plus, Search, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { StewardSessionSummary } from '@/lib/api'

interface Props {
  name: string | null
  sessions: StewardSessionSummary[]
  activeId: string | null
  onOpen?: () => void
  onNew: () => void
  onSelect: (id: string) => void
  onPin: (id: string, pinned: boolean) => void
  onDelete: (id: string) => void
}

/**
 * Session picker — the "Name ▾" control + popover, modeled on the concierge's
 * sidebar picker. Lists sessions (pinned-first, server-ordered), filterable by
 * name, with "+ New conversation" pinned at the top. Pin / delete are inline.
 */
export function StewardSessionPicker({
  name,
  sessions,
  activeId,
  onOpen,
  onNew,
  onSelect,
  onPin,
  onDelete,
}: Props) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)

  // Always pull a fresh list when the picker opens — never show a stale/empty list
  // just because the mount fetch raced auth or missed a later turn.
  function toggleOpen() {
    setOpen((o) => {
      const next = !o
      if (next) onOpen?.()
      return next
    })
  }

  // Close on outside click.
  useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const q = query.trim().toLowerCase()
  const filtered = q
    ? sessions.filter((s) => (s.name || '').toLowerCase().includes(q))
    : sessions

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={toggleOpen}
        className="inline-flex max-w-[260px] items-center gap-1.5 rounded-md px-2 py-1 text-[13px] font-light text-foreground hover:bg-muted/50"
      >
        <span className="truncate">{name || 'New conversation'}</span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      </button>

      {open && (
        <div className="absolute left-0 z-50 mt-1 w-[300px] overflow-hidden rounded-lg border border-border bg-background shadow-lg">
          {/* Search */}
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search sessions…"
              className="w-full bg-transparent text-[13px] font-light outline-none placeholder:text-muted-foreground"
            />
          </div>

          {/* New conversation */}
          <button
            type="button"
            onClick={() => {
              onNew()
              setOpen(false)
            }}
            className="flex w-full items-center gap-2 border-b border-border px-3 py-2 text-left text-[13px] font-light text-foreground hover:bg-muted/50"
          >
            <Plus className="h-3.5 w-3.5 shrink-0" />
            New conversation
          </button>

          {/* List */}
          <div className="max-h-[320px] overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-[12px] font-light text-muted-foreground">
                {sessions.length === 0 ? 'No conversations yet.' : 'No matches.'}
              </div>
            ) : (
              filtered.map((s) => (
                <div
                  key={s.session_id}
                  className={cn(
                    'group flex items-center gap-2 px-3 py-1.5 text-[13px] font-light',
                    s.session_id === activeId ? 'bg-muted/60' : 'hover:bg-muted/40'
                  )}
                >
                  <button
                    type="button"
                    onClick={() => {
                      onSelect(s.session_id)
                      setOpen(false)
                    }}
                    className="flex min-w-0 flex-1 flex-col items-start text-left"
                  >
                    <span className="w-full truncate text-foreground">
                      {s.name || 'Untitled'}
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      {relativeTime(s.updated_at || s.created_at)}
                      {s.total_turns ? ` · ${Math.ceil(s.total_turns / 2)} turns` : ''}
                    </span>
                  </button>

                  <button
                    type="button"
                    title={s.pinned ? 'Unpin' : 'Pin'}
                    onClick={() => onPin(s.session_id, !s.pinned)}
                    className={cn(
                      'shrink-0 rounded p-1 hover:bg-muted',
                      s.pinned
                        ? 'text-foreground'
                        : 'text-muted-foreground opacity-0 group-hover:opacity-100'
                    )}
                  >
                    {s.pinned ? <Pin className="h-3.5 w-3.5" /> : <PinOff className="h-3.5 w-3.5" />}
                  </button>

                  <button
                    type="button"
                    title="Delete"
                    onClick={() => {
                      if (confirm(`Delete "${s.name || 'Untitled'}"?`)) onDelete(s.session_id)
                    }}
                    className="shrink-0 rounded p-1 text-muted-foreground opacity-0 hover:bg-muted hover:text-red-500 group-hover:opacity-100"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/** Compact relative time: "just now", "5m", "3h", "2d", else a date. */
function relativeTime(iso: string | null): string {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const diff = Date.now() - then
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h`
  const d = Math.floor(h / 24)
  if (d < 7) return `${d}d`
  return new Date(iso).toLocaleDateString()
}
